import CancelButton from "components/CancelButton";
import ConfirmBoxModal from "components/ConfirmBoxModal";
import SubmitHookFormTS from "components/SubmitHookFormTS";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import { FeeLevelType } from "../../types";
import {
  addStateFeeLevelFeeLevel,
  editStateFeeLevel,
  feeLevelSaving,
  rowEditIdStateFeeLevel,
  selectedRowsStateFeeLevel,
} from "../atoms/atoms";
import useAddFeeLevel from "../hooks/useAddFeeLevel";
import useEditFeeType from "../hooks/useUpdateFeeLevel";
import FeeLevelDetails from "./FeeLevelInput";

export const FeeLevelForm = () => {
  // atoms
  const [editData, setEditData] = useRecoilState(rowEditIdStateFeeLevel);
  const [isSaving, setIsSaving] = useRecoilState(feeLevelSaving);
  const [isAddState, setIsAddState] = useRecoilState(addStateFeeLevelFeeLevel);
  const [isEditState, setIsEditState] = useRecoilState(editStateFeeLevel);
  const [, setSelectedRows] = useRecoilState(selectedRowsStateFeeLevel);

  // hooks
  const addFee = useAddFeeLevel();
  const updateLicenseType = useEditFeeType();

  // local
  const [newData, setNewData] = useState<FeeLevelType>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);

  // form methods
  const useFormMethods = useForm({
    defaultValues: { ...(editData as unknown as FeeLevelType) },
  });

  const onSubmit = (data: FeeLevelType) => {
    const value = {
      ...data,
      feeLevelName: data.feeLevelName.trim(),
    };

    setNewData(value);
    setSaveModal(true);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(null);
    setIsAddState(false);
    setIsEditState(false);
    setSelectedRows([]);
  };

  const handleSave = () => {
    if (newData) {
      if (isEditState) {
        updateLicenseType(newData);
      } else {
        addFee(newData);
      }
    }
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(null);
    setIsAddState(false);
    setIsEditState(false);
  };

  return (
    <>
      <div className="px-2 py-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState ? "Add New Fee Level" : "Edit Fee Level"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookFormTS
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <FeeLevelDetails data={editData!} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Fee Level" : "Update Fee Level"}
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
            setIsSaving(false);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};
